import i18n from '@/i18n/i18n';
import type {
  ProcessingInsightErrorDto,
  ProcessingRejectionViolationDto,
} from '@/types/dtos/media/processing-insight.dto';
import type { TranslationKey } from '@/i18n/translation-key';

const POLICY_MESSAGE_PREFIX = 'media.processing.policy.';

type Translate = (key: TranslationKey, params?: Record<string, string | number>) => string;

export type PolicyViolationShape = Pick<
  ProcessingRejectionViolationDto,
  'rule' | 'message' | 'variant' | 'extension'
> & {
  details?: Record<string, unknown>;
};

const NESTED_POLICY_DEFAULT_VARIANT: Record<string, string> = {
  POLICY_INVALID_PACKAGE: 'invalid_structure',
  POLICY_PDF_ACTIVE_CONTENT: 'generic',
  POLICY_DISALLOWED_EMBED: 'default',
};

const DISALLOWED_EMBED_I18N_VARIANTS = new Set(['mov']);

function policyRuleKey(rule: string, variant?: string): string {
  if (variant) {
    return `${POLICY_MESSAGE_PREFIX}${rule}.${variant}`;
  }
  return `${POLICY_MESSAGE_PREFIX}${rule}`;
}

function isPolicyTranslationKey(value: string): boolean {
  return value.trim().startsWith(POLICY_MESSAGE_PREFIX);
}

function isTranslationMissing(key: string, translated: string): boolean {
  const trimmed = translated.trim();
  if (!trimmed || trimmed === key) {
    return true;
  }
  if (trimmed.includes('returned an object instead of string')) {
    return true;
  }
  if (isPolicyTranslationKey(trimmed) && trimmed === key) {
    return true;
  }
  return false;
}

function translatePolicyKey(
  key: string,
  params: Record<string, string | number> | undefined,
  t?: Translate,
): string | undefined {
  if (t) {
    const viaHook = t(key as TranslationKey, params);
    if (!isTranslationMissing(key, viaHook)) {
      return viaHook.trim();
    }
  }

  if (!i18n.isInitialized) {
    return undefined;
  }

  const viaI18n = String(i18n.t(key, params)).trim();
  if (!isTranslationMissing(key, viaI18n)) {
    return viaI18n;
  }

  return undefined;
}

function inferPdfActiveContentVariantFromDetails(details?: Record<string, unknown>): string | undefined {
  if (!details) {
    return undefined;
  }
  if (typeof details.variant === 'string') {
    return details.variant;
  }
  const hasJavaScript = details.hasJavaScript === true;
  const hasEmbeddedFiles = details.hasEmbeddedFiles === true;
  if (hasJavaScript && hasEmbeddedFiles) {
    return 'scripts_and_attachments';
  }
  if (hasJavaScript) {
    return 'scripts_only';
  }
  if (hasEmbeddedFiles) {
    return 'attachments_only';
  }
  return undefined;
}

function inferPdfActiveContentVariantFromLegacyMessage(message: string): string | undefined {
  const normalized = message.toLowerCase();
  const mentionsScripts = normalized.includes('script') || normalized.includes('interactive');
  const mentionsAttachments = normalized.includes('attached file') || normalized.includes('attachments');
  if (mentionsScripts && mentionsAttachments) {
    return 'scripts_and_attachments';
  }
  if (mentionsAttachments) {
    return 'attachments_only';
  }
  if (mentionsScripts) {
    return 'scripts_only';
  }
  return undefined;
}

function trimTrailingZero(value: string): string {
  return value.endsWith('.0') ? value.slice(0, -2) : value;
}

function formatPolicyByteLabel(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '0 B';
  }
  const gib = 1024 ** 3;
  const mib = 1024 ** 2;
  const kib = 1024;
  if (bytes >= gib) {
    return `${trimTrailingZero((bytes / gib).toFixed(1))} GB`;
  }
  if (bytes >= mib) {
    return `${trimTrailingZero((bytes / mib).toFixed(1))} MB`;
  }
  if (bytes >= kib) {
    return `${trimTrailingZero((bytes / kib).toFixed(1))} KB`;
  }
  return `${Math.round(bytes)} B`;
}

function buildPolicyViolationParams(
  normalized: PolicyViolationShape,
  details?: Record<string, unknown>,
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (normalized.extension) {
    params.extension = normalized.extension;
  }
  if (!details) {
    return params;
  }

  const entryName =
    typeof details.entryName === 'string'
      ? details.entryName
      : typeof details.entryPath === 'string'
        ? (details.entryPath.split('/').pop() ?? details.entryPath)
        : typeof details.path === 'string'
          ? (details.path.split('/').pop() ?? details.path)
          : undefined;
  if (entryName) {
    params.entryName = entryName;
  }

  const sizeLabel =
    typeof details.sizeLabel === 'string'
      ? details.sizeLabel
      : typeof details.sizeBytes === 'number'
        ? formatPolicyByteLabel(details.sizeBytes)
        : typeof details.maxSingleEntryBytes === 'number'
          ? formatPolicyByteLabel(details.maxSingleEntryBytes)
          : typeof details.uncompressedBytes === 'number'
            ? formatPolicyByteLabel(details.uncompressedBytes)
            : undefined;
  if (sizeLabel) {
    params.sizeLabel = sizeLabel;
  }

  const limitLabel =
    typeof details.limitLabel === 'string'
      ? details.limitLabel
      : typeof details.limitBytes === 'number'
        ? formatPolicyByteLabel(details.limitBytes)
        : typeof details.maxUncompressedBytes === 'number'
          ? formatPolicyByteLabel(details.maxUncompressedBytes)
          : undefined;
  if (limitLabel) {
    params.limitLabel = limitLabel;
  }

  const entryCount =
    typeof details.entryCount === 'number'
      ? details.entryCount
      : typeof details.packageEntryCount === 'number'
        ? details.packageEntryCount
        : undefined;
  if (entryCount != null) {
    params.entryCount = entryCount;
  }

  const limitEntries =
    typeof details.limitEntries === 'number'
      ? details.limitEntries
      : typeof details.maxEntries === 'number'
        ? details.maxEntries
        : undefined;
  if (limitEntries != null) {
    params.limitEntries = limitEntries;
  }

  const slideCount =
    typeof details.count === 'number'
      ? details.count
      : typeof details.slideCount === 'number'
        ? details.slideCount
        : undefined;
  if (slideCount != null) {
    params.slideCount = slideCount;
  }

  const slideLimit =
    typeof details.maxPages === 'number'
      ? details.maxPages
      : typeof details.slideLimit === 'number'
        ? details.slideLimit
        : undefined;
  if (slideLimit != null) {
    params.slideLimit = slideLimit;
  }

  const slideNumber = typeof details.slideNumber === 'number' ? details.slideNumber : undefined;
  if (slideNumber != null) {
    params.slideNumber = slideNumber;
  }

  const linkCount = typeof details.linkCount === 'number' ? details.linkCount : undefined;
  if (linkCount != null) {
    params.linkCount = linkCount;
  }

  const linkTarget = typeof details.linkTarget === 'string' ? details.linkTarget : undefined;
  if (linkTarget) {
    params.linkTarget = linkTarget;
  }

  const linkTargets =
    typeof details.linkTargets === 'string'
      ? details.linkTargets
      : Array.isArray(details.targets)
        ? details.targets.filter((value): value is string => typeof value === 'string').join('; ')
        : undefined;
  if (linkTargets) {
    params.linkTargets = linkTargets;
  }

  const fontNamesPreview =
    typeof details.fontNamesPreview === 'string'
      ? details.fontNamesPreview
      : Array.isArray(details.fontNames)
        ? details.fontNames.filter((value): value is string => typeof value === 'string').join(', ')
        : undefined;
  if (fontNamesPreview) {
    params.fontNamesPreview = fontNamesPreview;
  }

  const fontName = typeof details.fontName === 'string' ? details.fontName : undefined;
  if (fontName) {
    params.fontName = fontName;
  }

  const extraCount = typeof details.extraCount === 'number' ? details.extraCount : undefined;
  if (extraCount != null) {
    params.extraCount = extraCount;
  }

  return params;
}

function resolvePolicyVariant(violation: PolicyViolationShape): string | undefined {
  const normalized = normalizeProcessingPolicyViolation(violation);

  if (normalized.rule === 'POLICY_DISALLOWED_EMBED') {
    const ext = normalized.extension?.trim().toLowerCase();
    if (ext && DISALLOWED_EMBED_I18N_VARIANTS.has(ext)) {
      return ext;
    }
    return 'default';
  }

  if (normalized.rule === 'POLICY_MISSING_FONTS') {
    if (violation.details?.fontCount === 1) {
      return 'single';
    }
    const extraCount = typeof violation.details?.extraCount === 'number' ? violation.details.extraCount : 0;
    if (extraCount > 0) {
      return 'list_extra';
    }
    return typeof violation.details?.variant === 'string' ? violation.details.variant : 'list';
  }

  if (normalized.variant) {
    return normalized.variant;
  }

  if (normalized.rule === 'POLICY_PDF_ACTIVE_CONTENT') {
    return (
      inferPdfActiveContentVariantFromDetails(violation.details) ??
      inferPdfActiveContentVariantFromLegacyMessage(normalized.message) ??
      NESTED_POLICY_DEFAULT_VARIANT.POLICY_PDF_ACTIVE_CONTENT
    );
  }

  if (typeof violation.details?.variant === 'string') {
    return violation.details.variant;
  }

  return NESTED_POLICY_DEFAULT_VARIANT[normalized.rule];
}

export function normalizeProcessingPolicyViolation(violation: PolicyViolationShape): PolicyViolationShape {
  const details = violation.details;
  return {
    rule: violation.rule,
    message: violation.message,
    variant: violation.variant ?? (typeof details?.variant === 'string' ? details.variant : undefined),
    extension: violation.extension ?? (typeof details?.extension === 'string' ? details.extension : undefined),
    ...(details ? { details } : {}),
  };
}

export function resolveProcessingPolicyViolationMessage(violation: PolicyViolationShape, t?: Translate): string {
  const normalized = normalizeProcessingPolicyViolation(violation);
  const variant = resolvePolicyVariant(violation);
  const params = buildPolicyViolationParams(normalized, violation.details);

  const keysToTry: string[] = [];
  if (variant) {
    if (variant === 'entry_too_large' && params.sizeLabel && params.limitLabel) {
      keysToTry.push(policyRuleKey(normalized.rule, params.entryName ? 'entry_too_large' : 'entry_too_large_no_name'));
    } else if (normalized.rule === 'POLICY_MISSING_FONTS') {
      if (variant === 'single' && (params.fontName || params.fontNamesPreview)) {
        if (!params.fontName && params.fontNamesPreview) {
          params.fontName = params.fontNamesPreview;
        }
        keysToTry.push(policyRuleKey(normalized.rule, 'single'));
      } else if (params.fontNamesPreview) {
        keysToTry.push(policyRuleKey(normalized.rule, variant));
      }
    } else if (
      (variant === 'uncompressed' && params.sizeLabel && params.limitLabel) ||
      (variant === 'too_many_entries' && params.entryCount != null && params.limitEntries != null) ||
      ((variant === 'slides' || variant === 'pages') && params.slideCount != null && params.slideLimit != null) ||
      (normalized.rule === 'POLICY_EXTERNAL_LINK' &&
        ((variant === 'slide_single' && params.slideNumber != null && params.linkTarget) ||
          (variant === 'slide_multiple' &&
            params.slideNumber != null &&
            params.linkCount != null &&
            params.linkTargets) ||
          (variant === 'other_single' && params.linkTarget) ||
          (variant === 'other_multiple' && params.linkCount != null && params.linkTargets))) ||
      normalized.rule !== 'POLICY_PACKAGE_TOO_LARGE'
    ) {
      keysToTry.push(policyRuleKey(normalized.rule, variant));
    }
  }
  keysToTry.push(policyRuleKey(normalized.rule));

  for (const key of keysToTry) {
    const translated = translatePolicyKey(key, params, t);
    if (translated) {
      return translated;
    }
  }

  const message = normalized.message.trim();
  if (message && !isPolicyTranslationKey(message)) {
    return message;
  }

  if (normalized.rule.startsWith('POLICY_')) {
    return translatePolicyKey('media.processing.policy.summaryEmpty', undefined, t) ?? '';
  }

  return '';
}

export function policyViolationDedupeKey(violation: PolicyViolationShape): string {
  const details = violation.details ?? {};
  if (violation.rule === 'POLICY_DISALLOWED_EMBED') {
    const extension = violation.extension ?? (typeof details.extension === 'string' ? details.extension : undefined);
    return extension ? `${violation.rule}:${extension}` : violation.rule;
  }
  if (violation.rule === 'POLICY_PACKAGE_TOO_LARGE') {
    const variant = violation.variant ?? (typeof details.variant === 'string' ? details.variant : undefined);
    return variant ? `${violation.rule}:${variant}` : violation.rule;
  }
  if (violation.rule === 'POLICY_EXTERNAL_LINK') {
    const slideNumber = details.slideNumber;
    if (typeof slideNumber === 'number') {
      return `${violation.rule}:${slideNumber}`;
    }
    return `${violation.rule}:other`;
  }
  return violation.rule;
}

export function dedupeProcessingPolicyViolations(violations: PolicyViolationShape[]): PolicyViolationShape[] {
  const seen = new Set<string>();
  const result: PolicyViolationShape[] = [];
  for (const violation of violations) {
    const key = policyViolationDedupeKey(violation);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(violation);
  }
  return result;
}

export function resolveProcessingRejectionSummaryMessage(violations: PolicyViolationShape[], t?: Translate): string {
  const uniqueViolations = dedupeProcessingPolicyViolations(violations);
  if (uniqueViolations.length === 0) {
    return translatePolicyKey('media.processing.policy.summaryEmpty', undefined, t) ?? '';
  }
  if (uniqueViolations.length === 1) {
    return resolveProcessingPolicyViolationMessage(uniqueViolations[0], t);
  }

  const summaryKey = 'media.processing.policy.summaryMultiple';
  return (
    translatePolicyKey(summaryKey, { count: uniqueViolations.length }, t) ??
    `${uniqueViolations.length} compatibility issues were found with this file. Fix each one below, then upload again.`
  );
}

export function resolveProcessingErrorMessage(error: ProcessingInsightErrorDto, t?: Translate): string {
  const codeKey = `media.processing.policy.error.${error.code}`;
  const translated = translatePolicyKey(codeKey, undefined, t);
  if (translated) {
    return translated;
  }
  return error.message;
}

export function resolveProcessingPolicyViolations(insight: {
  rejection?: { code: string; message: string; violations?: PolicyViolationShape[] };
}): PolicyViolationShape[] {
  const violations = insight.rejection?.violations ?? [];
  if (violations.length > 0) {
    return dedupeProcessingPolicyViolations(violations);
  }
  if (!insight.rejection) {
    return [];
  }
  return [
    {
      rule: insight.rejection.code,
      message: insight.rejection.message,
    },
  ];
}

export function resolveProcessingPolicyAdvisories(insight: {
  advisories?: PolicyViolationShape[];
}): PolicyViolationShape[] {
  return dedupeProcessingPolicyViolations(insight.advisories ?? []);
}

export function hasProcessingAdvisories(insight?: { advisories?: PolicyViolationShape[] }): boolean {
  return resolveProcessingPolicyAdvisories(insight ?? {}).length > 0;
}

const DISPLAY_LIMITATION_RULES = new Set(['POLICY_MISSING_FONTS']);
const PLAYBACK_LIMITATION_RULES = new Set(['POLICY_PDF_EMBEDDED_VIDEO']);

export type ProcessingLimitationKind = 'display' | 'playback' | 'mixed';

export function resolveProcessingLimitationKind(advisories: PolicyViolationShape[]): ProcessingLimitationKind {
  if (advisories.length === 0) {
    return 'mixed';
  }

  const rules = new Set(advisories.map((advisory) => advisory.rule));
  const hasDisplay = [...rules].some((rule) => DISPLAY_LIMITATION_RULES.has(rule));
  const hasPlayback = [...rules].some((rule) => PLAYBACK_LIMITATION_RULES.has(rule));

  if (hasDisplay && !hasPlayback) {
    return 'display';
  }
  if (hasPlayback && !hasDisplay) {
    return 'playback';
  }
  return 'mixed';
}
